/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/platform-admin/platformAdmin.ts
 * PURPOSE: Platform admin API service layer with complete CRUD operations
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 * - BR: docs/01-planning/01-requirements/03-tenant-management/BR-TENM-010-BILLING-SUBSCRIPTION.md
 * - Backend: backend/app/api/v1/platform_admin.py (GET .../usage, GET .../downgrade-preview, PATCH .../tier)
 *  */

import { apiRequest } from '../config';

// Types matching backend API contracts
export interface PlatformAdminLogin {
  email_or_username: string;
  password: string;
}

export interface PlatformAdminAuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  admin: {
    id: string;
    email: string;
    username: string;
    full_name: string;
    role: string;
    is_active: boolean;
    is_verified: boolean;
  };
}

export interface PlatformAdminResponse {
  id: string;
  email: string;
  username: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  mfa_enabled: boolean;
  force_change_on_next_login?: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Reset Platform Admin Password Request (ADR-027 Bflow Alignment)
 *
 * Per CTO Mandate (Jan 15, 2026):
 * - Only PLATFORM_OPERATOR can reset passwords
 * - Cannot reset own password
 * - Reason is REQUIRED for audit trail
 * - All sessions are revoked on password reset
 *
 * @cross-references
 * - API: POST /platform-admin/admins/{admin_id}/reset-password
 * - ADR: docs/02-design/18-adrs/ADR-platform-admin-bflow-alignment.md
 */
export interface ResetPlatformAdminPasswordRequest {
  mode: 'generate_temporary' | 'set_temporary';
  temporary_password?: string;
  force_change_on_next_login: boolean;
  reason: string;
  ticket_id?: string;
}

export interface ResetPlatformAdminPasswordResponse {
  message: string;
  admin_id: string;
  admin_email: string;
  temporary_password?: string;  // Only returned when mode = 'generate_temporary'
  sessions_revoked: number;
  force_change_on_next_login: boolean;
}

export interface PlatformAdminCreate {
  email: string;
  username: string;
  password: string;
  full_name: string;
  role: string;
  notes?: string;
}

export interface PlatformAdminUpdate {
  full_name?: string;
  role?: string;
  notes?: string;
  is_active?: boolean;
}

/**
 * TenantCreateRequest interface - matches backend API contract
 *
 * Per OPTION-C-INVESTIGATION-ANALYSIS.md and DATABASE-SCHEMA.md:
 * - admin_password is NOW REQUIRED for tenant admin authentication
 * - Password validation: 8+ chars, 1 number, 1 uppercase, 1 lowercase
 * - Admin user created in public.users with tenant_id reference
 *
 * @cross-references
 * - Design: docs/02-design/02-database-design/DATABASE-SCHEMA.md
 * - API: docs/03-integrate/02-api-specifications/openapi-specs/auth-register.yaml
 */
export interface TenantCreateRequest {
  name: string;
  admin_email: string;
  admin_password: string;  // REQUIRED: Initial password for tenant admin
  admin_full_name?: string;  // Optional: Defaults to '{tenant_name} Admin'
  tier: string;
  configuration?: Record<string, any>;
  notes?: string;
}

export interface TenantResponse {
  id: number;
  tenant_key: string;
  name: string;
  display_name: string;
  schema_name: string;
  status: string;
  tier: string;
  admin_email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TenantFilters {
  status_filter?: string;
  tier_filter?: string;
  skip?: number;
  limit?: number;
}

export interface AdminFilters {
  role?: string;
  is_active?: boolean;
  skip?: number;
  limit?: number;
}

// =============================================================================
// SPRINT 28: Account Lockout Types (ADR-039 Track A)
// =============================================================================

/**
 * Account lockout status response
 * Per ADR-039: 5 failed attempts = 30 minute lockout
 */
export interface AccountLockoutStatusResponse {
  admin_id: string;
  email: string;
  is_locked: boolean;
  failed_login_attempts: number;
  locked_until: string | null;
  last_failed_login: string | null;
  lockout_reason: string | null;
  unlocked_at: string | null;
  unlocked_by: string | null;
}

/**
 * Unlock account request - SECURITY_ADMIN only
 */
export interface UnlockAccountRequest {
  reason: string;
  ticket_id?: string;
}

export interface UnlockAccountResponse {
  admin_id: string;
  admin_email: string;
  unlocked: boolean;
  unlocked_by: string;
  reason: string;
  correlation_id: string;
}

// =============================================================================
// SPRINT 28: IP Whitelist Types (ADR-039 Track C)
// =============================================================================

export interface IPWhitelistEntry {
  id: string;
  ip_address: string;
  cidr_prefix: number | null;
  description: string | null;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string | null;
  expires_at: string | null;
}

export interface IPWhitelistCreateRequest {
  ip_address: string;
  cidr_prefix?: number;
  description?: string;
  expires_at?: string;
}

export interface IPWhitelistListResponse {
  total: number;
  items: IPWhitelistEntry[];
}

export interface IPWhitelistCheckResponse {
  ip_address: string;
  is_whitelisted: boolean;
  matched_entry_id: string | null;
}

// =============================================================================
// BR-TENM-010: Tenant Tier Management Types (usage / downgrade-preview / change-tier)
// =============================================================================

/**
 * BR-TENM-010 FR-004 / DD-0048 §4.5: current usage vs tier limits for one resource.
 * `max === -1` (unlimited=true) means the tier has no cap on this resource.
 */
export interface TenantUsageMetric {
  current: number;
  max: number;
  percentage: number;
  unlimited: boolean;
}

export interface TenantUsageWarning {
  resource: string;
  message: string;
}

export interface TenantUsageResponse {
  tenant_key: string;
  tier: string;
  tier_display: string;
  usage: {
    users: TenantUsageMetric;
  };
  warnings: TenantUsageWarning[];
}

/** S98L-003 / DD-0048 Condition C — impact preview before PATCH .../tier */
export interface TenantDowngradePreviewResponse {
  current_tier: string;
  new_tier: string;
  new_tier_display: string;
  is_safe: boolean;
  excess_resources: {
    users: Array<{ id: string; email: string; full_name: string; role: string }>;
    branches: Array<{ id: string; name: string; code: string }>;
  };
  warnings: string[];
}

export interface TenantTierChangeRequest {
  new_tier: string;
  reason?: string;
}

export interface TenantTierChangeResponse {
  previous_tier: string;
  new_tier: string;
  new_limits: {
    max_users: number;
    max_branches: number;
    max_shifts: number;
  };
  effective_immediately: boolean;
}

// =============================================================================
// SPRINT 28: Audit Log Viewer Types (ADR-039 Track B)
// =============================================================================

export interface AuditLogEntry {
  id: string;
  event_type: string;
  correlation_id: string;
  actor_admin_id: string;
  actor_email: string | null;
  actor_roles: string[];
  entity_type: string;
  entity_id: string;
  before_state: Record<string, any> | null;
  after_state: Record<string, any> | null;
  details: Record<string, any> | null;
  ip_address: string | null;
  user_agent: string | null;
  platform_code: string;
  created_at: string;
}

export interface AuditLogListResponse {
  total: number;
  items: AuditLogEntry[];
  has_more: boolean;
}

export interface AuditLogFilters {
  event_type?: string;
  actor_admin_id?: string;
  entity_type?: string;
  entity_id?: string;
  correlation_id?: string;
  start_date?: string;
  end_date?: string;
  skip?: number;
  limit?: number;
}

/**
 * Platform Admin API Service
 * Handles all platform administrator operations including authentication,
 * admin management, and tenant management through platform admin privileges.
 */
export const platformAdminApi = {
  /**
   * Authentication Operations
   */
  async login(credentials: PlatformAdminLogin): Promise<PlatformAdminAuthResponse> {
    return apiRequest('/platform-admin/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });
  },

  async refresh(refreshToken: string): Promise<PlatformAdminAuthResponse> {
    return apiRequest('/platform-admin/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  },

  /**
   * Platform Admin Profile Operations
   */
  async getProfile(accessToken: string): Promise<PlatformAdminResponse> {
    return apiRequest('/platform-admin/me', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * Platform Admin Management Operations
   */
  async listAdmins(accessToken: string, filters?: AdminFilters): Promise<PlatformAdminResponse[]> {
    const queryParams = new URLSearchParams();
    if (filters?.role) queryParams.append('role', filters.role);
    if (filters?.is_active !== undefined) queryParams.append('is_active', filters.is_active.toString());
    if (filters?.skip) queryParams.append('skip', filters.skip.toString());
    if (filters?.limit) queryParams.append('limit', filters.limit.toString());

    const url = `/platform-admin/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    return apiRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  async createAdmin(accessToken: string, adminData: PlatformAdminCreate): Promise<PlatformAdminResponse> {
    return apiRequest('/platform-admin/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(adminData),
    });
  },

  async updateAdmin(
    accessToken: string,
    adminId: string,
    updates: PlatformAdminUpdate
  ): Promise<PlatformAdminResponse> {
    return apiRequest(`/platform-admin/${adminId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(updates),
    });
  },

  async deactivateAdmin(accessToken: string, adminId: string): Promise<void> {
    return apiRequest(`/platform-admin/${adminId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * Reset Platform Admin Password (ADR-027 Bflow Alignment)
   *
   * CTO Mandate (Jan 15, 2026):
   * - Only PLATFORM_OPERATOR can reset passwords
   * - Cannot reset own password (400 error)
   * - Reason field is REQUIRED for audit trail
   * - All active sessions are revoked on reset
   * - Includes X-Correlation-Id header for cross-service tracing
   *
   * @cross-references
   * - API: POST /platform-admin/admins/{admin_id}/reset-password
   * - ADR: docs/02-design/18-adrs/ADR-platform-admin-bflow-alignment.md
   */
  async resetPlatformAdminPassword(
    accessToken: string,
    adminId: string,
    resetData: ResetPlatformAdminPasswordRequest
  ): Promise<ResetPlatformAdminPasswordResponse> {
    // Generate correlation ID for request tracing
    const correlationId = crypto.randomUUID();

    return apiRequest(`/platform-admin/admins/${adminId}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-Correlation-Id': correlationId,
      },
      body: JSON.stringify(resetData),
    });
  },

  async getAdmin(accessToken: string, adminId: string): Promise<PlatformAdminResponse> {
    return apiRequest(`/platform-admin/${adminId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * Tenant Management Operations (Platform Admin Only)
   */
  async createTenant(accessToken: string, tenantData: TenantCreateRequest): Promise<TenantResponse> {
    return apiRequest('/platform-admin/tenants/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(tenantData),
    });
  },

  async listTenants(accessToken: string, filters?: TenantFilters): Promise<TenantResponse[]> {
    const queryParams = new URLSearchParams();
    if (filters?.status_filter) queryParams.append('status_filter', filters.status_filter);
    if (filters?.tier_filter) queryParams.append('tier_filter', filters.tier_filter);
    if (filters?.skip) queryParams.append('skip', filters.skip.toString());
    if (filters?.limit) queryParams.append('limit', filters.limit.toString());

    const url = `/platform-admin/tenants/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    return apiRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  async getTenant(accessToken: string, tenantKey: string): Promise<TenantResponse> {
    return apiRequest(`/platform-admin/tenants/${tenantKey}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  async updateTenantStatus(
    accessToken: string,
    tenantKey: string,
    newStatus: string
  ): Promise<{ message: string }> {
    return apiRequest(`/platform-admin/tenants/${tenantKey}/status?new_status=${newStatus}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * Reset tenant admin password.
   *
   * Per SDLC 6.3.1 Security Standards:
   * - Password validation: 8+ chars, 1 number, 1 uppercase, 1 lowercase
   * - Existing sessions invalidated via token_version increment
   *
   * @cross-references
   * - API: POST /platform-admin/tenants/{tenant_key}/reset-admin-password
   * - Design: docs/02-design/03-Security-Design/
   */
  async resetTenantAdminPassword(
    accessToken: string,
    tenantKey: string,
    newPassword: string
  ): Promise<{ message: string; tenant_key: string; admin_email: string }> {
    return apiRequest(`/platform-admin/tenants/${tenantKey}/reset-admin-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ new_password: newPassword }),
    });
  },

  /**
   * DD-0038 §2.1: Toggle tenant features (e.g. bflow_sync_enabled).
   * Only PLATFORM_SUPER_ADMIN can call this endpoint.
   *
   * @cross-references
   * - API: PATCH /platform-admin/tenants/{tenant_key}/features
   * - Design: docs/02-design/18-adrs/06-decisions/DD-0038-bflow-shift-import-ui-pa-toggle.md
   */
  async updateTenantFeatures(
    accessToken: string,
    tenantKey: string,
    features: Record<string, boolean>
  ): Promise<Record<string, any>> {
    return apiRequest(`/platform-admin/tenants/${tenantKey}/features`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(features),
    });
  },

  /**
   * DD-0005 §5.2: Update tenant operating mode with state-machine guards.
   *
   * @cross-references
   * - API: PATCH /platform-admin/tenants/{tenant_key}/operating-mode
   * - Design: docs/02-design/18-adrs/06-decisions/DD-0005-operating-mode-3-tier-tenant-config.md
   */
  async updateTenantOperatingMode(
    accessToken: string,
    tenantKey: string,
    payload: {
      operating_mode: string;
      new_erp_provider?: string;
      reason: string;
    }
  ): Promise<{
    tenant_key: string;
    operating_mode: string;
    previous_mode: string;
    erp_provider: string | null;
    side_effects: Record<string, any>;
    warnings: string[] | null;
  }> {
    return apiRequest(`/platform-admin/tenants/${tenantKey}/operating-mode`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });
  },

  /**
   * BR-TENM-010 §3.1 / DD-0048 §4.5: Get current usage vs tier limits.
   * Auth: Platform Admin (any role).
   *
   * @cross-references
   * - API: GET /platform-admin/tenants/{tenant_key}/usage
   */
  async getTenantUsage(accessToken: string, tenantKey: string): Promise<TenantUsageResponse> {
    return apiRequest(`/platform-admin/tenants/${tenantKey}/usage`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * S98L-003 / DD-0048 Condition C: Preview downgrade impact BEFORE PATCH .../tier.
   * Auth: PLATFORM_OPERATOR or BILLING_ADMIN (same as getTenantUsage's operator gate).
   *
   * @cross-references
   * - API: GET /platform-admin/tenants/{tenant_key}/downgrade-preview?new_tier=X
   */
  async previewTenantDowngrade(
    accessToken: string,
    tenantKey: string,
    newTier: string
  ): Promise<TenantDowngradePreviewResponse> {
    const params = new URLSearchParams({ new_tier: newTier });
    return apiRequest(`/platform-admin/tenants/${tenantKey}/downgrade-preview?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * BR-TENM-010 §3.2 FR-005/FR-006: Change tenant tier (upgrade always allowed;
   * downgrade validated server-side — current usage must <= new tier limits).
   * Auth: PLATFORM_OPERATOR or BILLING_ADMIN.
   *
   * @cross-references
   * - API: PATCH /platform-admin/tenants/{tenant_key}/tier
   */
  async changeTenantTier(
    accessToken: string,
    tenantKey: string,
    payload: TenantTierChangeRequest
  ): Promise<TenantTierChangeResponse> {
    return apiRequest(`/platform-admin/tenants/${tenantKey}/tier`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });
  },

  /**
   * DD-0038 §2.1 v1.2 / FR-008a: List available BFlow data sources.
   *
   * @cross-references
   * - API: GET /platform-admin/bflow-sources
   * - Design: DD-0038 §2.1, DD-0033 §5.1.7
   */
  async listBflowSources(
    accessToken: string
  ): Promise<{ sources: Array<{ tenant_id: string | null; label: string; shift_count: number }> }> {
    return apiRequest('/platform-admin/bflow-sources', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * Utility Methods
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  },

  /**
   * Platform Admin Statistics
   */
  async getStats(accessToken: string): Promise<any> {
    return apiRequest('/platform-admin/stats', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  // ===========================================================================
  // SPRINT 28: Account Lockout APIs (ADR-039 Track A)
  // ===========================================================================

  /**
   * Get account lockout status for a platform admin
   * @param accessToken - Bearer token
   * @param adminId - Target admin UUID
   */
  async getAccountLockoutStatus(
    accessToken: string,
    adminId: string
  ): Promise<AccountLockoutStatusResponse> {
    return apiRequest(`/platform-admin/admins/${adminId}/lockout-status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * Unlock a locked platform admin account
   * Requires SECURITY_ADMIN role
   *
   * @param accessToken - Bearer token (SECURITY_ADMIN or PLATFORM_SUPER_ADMIN)
   * @param adminId - Target admin UUID to unlock
   * @param unlockData - Reason and optional ticket_id
   */
  async unlockAdminAccount(
    accessToken: string,
    adminId: string,
    unlockData: UnlockAccountRequest
  ): Promise<UnlockAccountResponse> {
    const correlationId = crypto.randomUUID();

    return apiRequest(`/platform-admin/admins/${adminId}/unlock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-Correlation-Id': correlationId,
      },
      body: JSON.stringify(unlockData),
    });
  },

  // ===========================================================================
  // SPRINT 28: IP Whitelist APIs (ADR-039 Track C)
  // ===========================================================================

  /**
   * List all IP whitelist entries
   * @param accessToken - Bearer token
   * @param includeInactive - Include deactivated entries
   * @param skip - Pagination offset
   * @param limit - Max results (1-500)
   */
  async listIPWhitelist(
    accessToken: string,
    includeInactive: boolean = false,
    skip: number = 0,
    limit: number = 100
  ): Promise<IPWhitelistListResponse> {
    const params = new URLSearchParams({
      include_inactive: includeInactive.toString(),
      skip: skip.toString(),
      limit: limit.toString(),
    });

    return apiRequest(`/platform-admin/ip-whitelist?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * Add a new IP whitelist entry
   * Requires SECURITY_ADMIN role
   *
   * @param accessToken - Bearer token (SECURITY_ADMIN or PLATFORM_SUPER_ADMIN)
   * @param entryData - IP address and optional CIDR prefix
   */
  async addIPWhitelistEntry(
    accessToken: string,
    entryData: IPWhitelistCreateRequest
  ): Promise<IPWhitelistEntry> {
    const correlationId = crypto.randomUUID();

    return apiRequest('/platform-admin/ip-whitelist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-Correlation-Id': correlationId,
      },
      body: JSON.stringify(entryData),
    });
  },

  /**
   * Remove (deactivate) an IP whitelist entry
   * Requires SECURITY_ADMIN role
   *
   * @param accessToken - Bearer token (SECURITY_ADMIN or PLATFORM_SUPER_ADMIN)
   * @param entryId - UUID of the entry to remove
   */
  async removeIPWhitelistEntry(
    accessToken: string,
    entryId: string
  ): Promise<{ message: string; entry_id: string }> {
    const correlationId = crypto.randomUUID();

    return apiRequest(`/platform-admin/ip-whitelist/${entryId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'X-Correlation-Id': correlationId,
      },
    });
  },

  /**
   * Check if an IP address is whitelisted
   * Useful for testing whitelist rules
   *
   * @param accessToken - Bearer token
   * @param ipAddress - IP address to check
   */
  async checkIPWhitelisted(
    accessToken: string,
    ipAddress: string
  ): Promise<IPWhitelistCheckResponse> {
    const params = new URLSearchParams({ ip_address: ipAddress });

    return apiRequest(`/platform-admin/ip-whitelist/check?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  // ===========================================================================
  // SPRINT 28: Audit Log Viewer APIs (ADR-039 Track B)
  // ===========================================================================

  /**
   * List audit logs with filtering
   * @param accessToken - Bearer token
   * @param filters - Optional filters (event_type, actor, entity, date range)
   */
  async listAuditLogs(
    accessToken: string,
    filters?: AuditLogFilters
  ): Promise<AuditLogListResponse> {
    const params = new URLSearchParams();

    if (filters?.event_type) params.append('event_type', filters.event_type);
    if (filters?.actor_admin_id) params.append('actor_admin_id', filters.actor_admin_id);
    if (filters?.entity_type) params.append('entity_type', filters.entity_type);
    if (filters?.entity_id) params.append('entity_id', filters.entity_id);
    if (filters?.correlation_id) params.append('correlation_id', filters.correlation_id);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.skip !== undefined) params.append('skip', filters.skip.toString());
    if (filters?.limit !== undefined) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const url = `/platform-admin/audit-logs${queryString ? `?${queryString}` : ''}`;

    return apiRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },

  /**
   * Export audit logs to CSV
   * Downloads a CSV file with audit log data
   *
   * @param accessToken - Bearer token
   * @param filters - Optional filters (event_type, actor, entity, date range)
   * @returns Blob containing CSV data
   */
  async exportAuditLogs(
    accessToken: string,
    filters?: Omit<AuditLogFilters, 'skip' | 'limit'>
  ): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters?.event_type) params.append('event_type', filters.event_type);
    if (filters?.actor_admin_id) params.append('actor_admin_id', filters.actor_admin_id);
    if (filters?.entity_type) params.append('entity_type', filters.entity_type);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const queryString = params.toString();
    const url = `/platform-admin/audit-logs/export${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api/v1'}${url}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to export audit logs: ${response.statusText}`);
    }

    return response.blob();
  },

  /**
   * Helper to download exported audit logs
   * @param blob - CSV blob from exportAuditLogs
   * @param filename - Optional custom filename
   */
  downloadAuditLogsCSV(blob: Blob, filename?: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  async refreshTokenIfNeeded(
    accessToken: string,
    refreshToken: string
  ): Promise<{ accessToken: string; refreshToken: string } | null> {
    if (!this.isTokenExpired(accessToken)) {
      return null; // Token is still valid
    }

    try {
      const response = await this.refresh(refreshToken);
      return {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      };
    } catch (error) {
      // Refresh failed, user needs to login again
      throw new Error('Session expired. Please login again.');
    }
  },
};

export default platformAdminApi;